import {
  BUDGET_TYPE,
  APPROVAL_FLOW,
  OTR_STATUS
} from '../constants/otrEnums';

/**
 * Calculate trip days:
 * Date of Return - Date of Travel + 1
 */
export const calculateTripDays = (dateOfTravel, dateOfReturn) => {
  if (!dateOfTravel || !dateOfReturn) return 0;

  const start = new Date(dateOfTravel);
  const end = new Date(dateOfReturn);

  const diffTime = end.getTime() - start.getTime();
  const diffDays =
    Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

  return diffDays > 0 ? diffDays : 0;
};

/**
 * Calculate budget for an expense head based on its policy.
 */
export const calculateHeadBudget = (policyHead, tripDays) => {
  const { budgetType, defaultAmount } = policyHead;

  switch (budgetType) {
    case BUDGET_TYPE.PER_DAY:
      return (defaultAmount || 0) * tripDays;

    case BUDGET_TYPE.LUMPSUM:
      return defaultAmount || 0;

    case BUDGET_TYPE.AT_ACTUALS:
    default:
      return 0;
  }
};

/**
 * Calculate total budget for a list of policy heads.
 */
export const calculateTotalBudget = (policyHeads, tripDays) => {
  return policyHeads.reduce(
    (total, head) =>
      total + calculateHeadBudget(head, tripDays),
    0
  );
};

/**
 * Calculate settlement amount.
 */
export const calculateSettlement = (
  approvedExpenses,
  advance
) => {
  return (approvedExpenses || 0) - (advance || 0);
};

/**
 * Final Advance permission.
 */
export const getFinalAdvanceEditorRole = (approvalFlow) => {
  switch (approvalFlow) {
    case APPROVAL_FLOW.MANAGER:
      return 'manager';

    case APPROVAL_FLOW.FINANCE:
    case APPROVAL_FLOW.MANAGER_THEN_FINANCE:
    case APPROVAL_FLOW.FINANCE_THEN_MANAGER:
      return 'finance';

    default:
      return null;
  }
};

export const canEditFinalAdvance = (
  approvalFlow,
  currentUserRole,
  status
) => {
  const editorRole =
    getFinalAdvanceEditorRole(approvalFlow);

  if (!editorRole || !currentUserRole) {
    return false;
  }

  if (currentUserRole !== editorRole) {
    return false;
  }

  return [
    OTR_STATUS.SUBMITTED,
    OTR_STATUS.L1_APPROVED
  ].includes(status);
};

/**
 * This is retained for compatibility with existing callers.
 *
 * Approval status calculation is ultimately controlled by the backend.
 * The frontend should not send this value to the backend.
 */
export const getApprovalNextStatus = (
  approvalFlow,
  currentUserRole,
  currentStatus
) => {
  const isTwoStage =
    approvalFlow === APPROVAL_FLOW.MANAGER_THEN_FINANCE ||
    approvalFlow === APPROVAL_FLOW.FINANCE_THEN_MANAGER;

  if (!isTwoStage) {
    return OTR_STATUS.APPROVED;
  }

  const firstRole =
    approvalFlow === APPROVAL_FLOW.MANAGER_THEN_FINANCE
      ? 'manager'
      : 'finance';

  const isFirstStage =
    currentStatus === OTR_STATUS.SUBMITTED &&
    currentUserRole === firstRole;

  return isFirstStage
    ? OTR_STATUS.L1_APPROVED
    : OTR_STATUS.APPROVED;
};

/**
 * Determine which approval actions should be displayed.
 *
 * IMPORTANT:
 * There is intentionally NO frontend isManager/isFinance check here.
 *
 * The backend is responsible for determining whether the authenticated
 * employee is actually allowed to perform the action.
 */
export const deriveOTRPermissions = (request = {}) => {
  const backendPermissions =
    request.permissions || request.actions;

  if (
    backendPermissions &&
    typeof backendPermissions === 'object'
  ) {
    return {
      canApprove: !!backendPermissions.canApprove,
      canReject: !!backendPermissions.canReject,
      canSendBack: !!backendPermissions.canSendBack,
      canEditCityTier: !!backendPermissions.canEditCityTier,
      canEditAdvance: !!backendPermissions.canEditAdvance,
    };
  }

  // Awaiting an approval step: either the travel request itself or,
  // once the employee submits expenses, the expense claim.
  const isPending = [
    OTR_STATUS.SUBMITTED,
    OTR_STATUS.L1_APPROVED,
    OTR_STATUS.EXPENSES_SUBMITTED,
    OTR_STATUS.EXPENSES_L1_APPROVED
  ].includes(request.status);

  return {
    canApprove: isPending,
    canReject: isPending,
    canSendBack: isPending,
    canEditCityTier: isPending && !request.tierId,

    // Final advance still follows the configured flow.
    // This is an editing permission, not approval authorization.
    canEditAdvance: false,
  };
};

// ---- Approval gate for expense tabs ----
// Statuses from the point the travel request itself is approved onwards.
const APPROVED_OR_LATER = [
  OTR_STATUS.APPROVED,
  OTR_STATUS.EXPENSES_SUBMITTED,
  OTR_STATUS.EXPENSES_L1_APPROVED,
  OTR_STATUS.EXPENSES_APPROVED,
];

/**
 * A request sent back while it was at the expense-claim stage (the backend
 * records where it was sent back from in `sentBackFrom`).
 */
export const isExpenseStageSentBack = (status, sentBackFrom) =>
  status === OTR_STATUS.SENT_BACK && /expense/i.test(String(sentBackFrom || ''));

/** Expense Report and Daily Expense Entry are available only after approval. */
export const isRequestApproved = (status, sentBackFrom) =>
  APPROVED_OR_LATER.includes(status) || isExpenseStageSentBack(status, sentBackFrom);

/** Expense lines can be filed while approved, or re-opened after an expense send-back. */
export const canAddExpenseLines = (status, sentBackFrom) =>
  status === OTR_STATUS.APPROVED || isExpenseStageSentBack(status, sentBackFrom);

/**
 * The travel request form itself is editable only as a Draft or when sent back
 * from the request stage. Rejected / Cancelled / in-approval requests are final.
 */
export const canEditTravelRequest = (status, sentBackFrom) =>
  status === OTR_STATUS.DRAFT ||
  (status === OTR_STATUS.SENT_BACK && !isExpenseStageSentBack(status, sentBackFrom));

/**
 * Daily expenses can be entered only once the trip has started, i.e. today
 * (local date) is the travel start date or later. Compares yyyy-mm-dd strings.
 */
export const hasTravelStarted = (dateOfTravel) => {
  const m = String(dateOfTravel || '').match(/^(\d{4}-\d{2}-\d{2})/);
  if (!m) return false;
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  return today >= m[1];
};

export const EXPENSE_LOCK_MESSAGE =
  'Available once the travel request is approved.';

export const EXPENSE_TABS = ['dailyExpense'];